import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FaCheck } from 'react-icons/fa';
import Section from '../components/common/Section';

const PricingContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
  margin-top: 3rem;
`;

const PricingCard = styled.div`
  background-color: white;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.medium};
  padding: 2rem;
  width: 300px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;

  ${props => props.featured && `
    transform: scale(1.05);
    box-shadow: ${props.theme.shadows.large};
    border: 2px solid ${props.theme.colors.primary};

    &::before {
      content: '推荐';
      position: absolute;
      top: 0;
      right: 0;
      background-color: ${props.theme.colors.primary};
      color: white;
      padding: 0.5rem 1rem;
      font-size: 0.8rem;
      font-weight: bold;
      transform: translateX(30%) translateY(-10%) rotate(45deg);
      transform-origin: top left;
      width: 100px;
      text-align: center;
    }
  `}

  &:hover {
    transform: ${props => props.featured ? 'scale(1.08)' : 'scale(1.03)'};
    box-shadow: ${props => props.theme.shadows.large};
  }
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  text-align: center;
`;

const PlanPrice = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  text-align: center;
  margin: 1.5rem 0;

  span {
    font-size: 1rem;
    color: ${props => props.theme.colors.lightText};
    font-weight: normal;
  }
`;

const OnboardingFee = styled.div`
  font-size: 1rem;
  color: ${props => props.theme.colors.lightText};
  text-align: center;
  margin-bottom: 1.5rem;
  font-style: italic;
`;

const PlanDescription = styled.p`
  color: ${props => props.theme.colors.lightText};
  text-align: center;
  margin-bottom: 1.5rem;
  min-height: 60px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: ${props => props.included ? props.theme.colors.darkText : props.theme.colors.lightText};

  svg {
    color: ${props => props.included ? props.theme.colors.success : props.theme.colors.lightGray};
    margin-right: 0.5rem;
    flex-shrink: 0;
  }
`;

const PlanButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.8rem;
  background-color: ${props => props.featured ? props.theme.colors.primary : 'white'};
  color: ${props => props.featured ? 'white' : props.theme.colors.primary};
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.small};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const FaqSection = styled.div`
  margin-top: 4rem;
`;

const FaqTitle = styled.h3`
  font-size: 1.8rem;
  text-align: center;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.primary};
`;

const FaqItem = styled.div`
  margin-bottom: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.lightGray};
  padding-bottom: 1.5rem;

  &:last-child {
    border-bottom: none;
  }
`;

const Question = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.darkText};
`;

const Answer = styled.p`
  color: ${props => props.theme.colors.lightText};
  line-height: 1.6;
`;

const Pricing = () => {
  const { t } = useTranslation();

  const plans = [
    {
      name: 'Basic',
      price: '$299',
      period: '/月',
      description: '适合小型企业的基础解决方案',
      features: [
        { text: '基础PIM产品功能', included: true },
        { text: '2个渠道对接账号(基础平台)', included: true },
        { text: '5GB存储空间', included: true },
        { text: '基础技术支持', included: true },
        { text: '每月数据报告', included: false },
        { text: '高级API集成', included: false },
        { text: 'AI 功能集成', included: false },
        { text: '专属客户经理', included: false }
      ],
      featured: false
    },
    {
      name: 'Premium',
      price: '$399',
      period: '/月',
      description: '适合中大型企业的全功能解决方案',
      features: [
        { text: '全部产品功能', included: true },
        { text: '5个渠道对接账号(高级平台)', included: true },
        { text: '10GB存储空间', included: true },
        { text: '优先技术支持', included: true },
        { text: '每月数据报告', included: true },
        { text: '高级API集成', included: true },
        { text: 'AI 功能集成', included: false },
        { text: '专属客户经理', included: false }
      ],
      featured: true
    },
    {
      name: 'Deluxe',
      price: '$1499',
      period: '/月',
      description: '适合大型企业的多渠道高级定制解决方案',
      features: [
        { text: '全部产品功能', included: true },
        { text: '无限对接全渠道账号', included: true },
        { text: '100GB存储空间', included: true },
        { text: '24/7技术支持', included: true },
        { text: '实时数据分析', included: true },
        { text: '高级API集成', included: true },
        { text: 'AI 功能集成', included: true },
        { text: '专属客户经理', included: true }
      ],
      featured: false
    }
  ];

  // 添加 faqs 数组定义
  const faqs = [
    {
      question: t('pricing.faq.q1'),
      answer: t('pricing.faq.a1')
    },
    {
      question: t('pricing.faq.q2'),
      answer: t('pricing.faq.a2')
    },
    {
      question: t('pricing.faq.q3'),
      answer: t('pricing.faq.a3')
    },
    {
      question: t('pricing.faq.q4'),
      answer: t('pricing.faq.a4')
    }
  ];

  return (
    <Section
      title={t('pricing.title')}
      subtitle={t('pricing.subtitle')}
    >
      <p>{t('pricing.content')}</p>

      <PricingContainer>
        {plans.map((plan, index) => (
          <PricingCard key={index} featured={plan.featured}>
            <PlanName>{plan.name}</PlanName>
            <PlanDescription>{plan.description}</PlanDescription>
            <PlanPrice>
              {plan.price}<span>{plan.period}</span>
            </PlanPrice>
            <OnboardingFee>+ Onboarding Fee</OnboardingFee>
            <FeatureList>
              {plan.features.map((feature, featureIndex) => (
                <Feature key={featureIndex} included={feature.included}>
                  <FaCheck /> {feature.text}
                </Feature>
              ))}
            </FeatureList>
            <PlanButton featured={plan.featured}>
              {t('pricing.selectPlan')}
            </PlanButton>
          </PricingCard>
        ))}
      </PricingContainer>

      <FaqSection>
        <FaqTitle>{t('pricing.faq.title')}</FaqTitle>
        {faqs.map((faq, index) => (
          <FaqItem key={index}>
            <Question>{faq.question}</Question>
            <Answer>{faq.answer}</Answer>
          </FaqItem>
        ))}
      </FaqSection>
    </Section>
  );
};

export default Pricing;